import {
  AttachMoney,
  DeleteOutline,
  Edit,
  ExpandMore,
  LocationOn,
  LocalOffer,
  VerifiedUser,
  Security,
} from '@mui/icons-material';
import {
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Grid2,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Nullable } from 'global';
import { formatCurrency } from 'utils';
import { Link as RouterLink } from 'react-router-dom';
import { useEquipmentDelivery } from 'modules/equipment';
import type { QuoteEquipmentResponse, QuoteFeeResponse } from '@trnsact/trnsact-shared-types';
import { DealBuilderEquipmentSource } from '../../../../types';
import { AcceptedMenuProduct } from '../../../../api';
import { FlatAccordion } from '../dealTab/FlatAccordion';
import { PriceField } from '../dealTab/PriceField';
import { AftermarketProductsCard, AttachmentsCard, EquipmentChargesCard } from './cards';

interface Props {
  item: DealBuilderEquipmentSource;
  quoteEquipment: Nullable<QuoteEquipmentResponse>;
  fees: QuoteFeeResponse[];
  insuranceProducts: AcceptedMenuProduct[];
  warrantyProducts: AcceptedMenuProduct[];
  onEdit: (equipmentId: string) => void;
  onDelete: (equipmentId: string) => void;
}

const formatCondition = (newUsed: Nullable<string>): string =>
  newUsed ? newUsed.charAt(0).toUpperCase() + newUsed.slice(1) : '-';

const DetailField = ({ label, value }: { label: string; value: Nullable<string> }) => (
  <Box>
    <Typography variant="body2" color="text.secondary">
      {label}:
    </Typography>
    <Typography fontWeight={500}>{value || '-'}</Typography>
  </Box>
);

export const EquipmentDetailCard = ({
  item,
  quoteEquipment,
  fees,
  insuranceProducts,
  warrantyProducts,
  onEdit,
  onDelete,
}: Props) => {
  const { deliveryAddress } = useEquipmentDelivery(item.equipmentId);
  const title = [item.year, item.make, item.model].filter(Boolean).join(' ');
  const addressLine = [deliveryAddress?.streetAddress].filter(Boolean).join(' ');
  const stateZip = [deliveryAddress?.state, deliveryAddress?.postalCode].filter(Boolean).join(' ');
  const addressLocality = [deliveryAddress?.city, stateZip].filter(Boolean).join(', ');
  const customerPrice = quoteEquipment?.price?.netPrice ?? item.retailPrice;
  const wholesale = quoteEquipment?.price?.wholesalePrice ?? item.wholesaleCost;
  const retail = quoteEquipment?.price?.retailPrice ?? item.retailPrice;
  const hasAttachments = (item.attachments?.length ?? 0) > 0;
  const hasEquipmentFees = fees.some(fee => fee.equipmentId === item.equipmentId);
  const hasInsurance = insuranceProducts.length > 0;
  const hasWarranty = warrantyProducts.length > 0;
  const hasAdditionalCards = hasAttachments || hasInsurance || hasWarranty || hasEquipmentFees;

  return (
    <Box p={2} borderRadius={1} border={1} borderColor="grey.200" bgcolor="#f7f8fa">
      <Stack gap={1}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
          <Stack direction="row" gap={0.5}>
            <Tooltip title="Edit Equipment" placement="top">
              <IconButton
                size="small"
                color="primary"
                className="icon-btn-bordered"
                onClick={() => {
                  onEdit(item.equipmentId);
                }}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Equipment" placement="top">
              <IconButton
                size="small"
                color="error"
                className="icon-btn-bordered"
                onClick={() => {
                  onDelete(item.equipmentId);
                }}
              >
                <DeleteOutline fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        <Stack direction="row" flexWrap="wrap" sx={{ columnGap: 6, rowGap: 2 }}>
          <DetailField label="Condition" value={formatCondition(item.newUsed)} />
          <DetailField label="Year" value={item.year} />
          <DetailField label="Make" value={item.make} />
          <DetailField label="Model" value={item.model} />
          <DetailField label="VIN" value={item.serialNumberVin} />
          <DetailField label="Mileage" value={item.mileage != null ? item.mileage.toLocaleString() : null} />
        </Stack>

        <Divider />

        <FlatAccordion disableGutters sx={{ bgcolor: '#f7f8fa' }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography fontWeight={500}>Additional Information</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack gap={2}>
              <Box>
                <Stack direction="row" alignItems="center" gap={0.5} mb={1}>
                  <AttachMoney color="primary" fontSize="small" />
                  <Typography variant="body1" fontWeight={400}>
                    Pricing & Margin
                  </Typography>
                </Stack>
                <Stack direction="row" flexWrap="wrap" sx={{ columnGap: 6, rowGap: 2 }}>
                  <PriceField label="Customer Price" value={customerPrice} />
                  <PriceField label="Wholesale" value={wholesale} />
                  <PriceField label="Retail" value={retail} />
                </Stack>
              </Box>

              {hasAdditionalCards && (
                <Grid2 container spacing={2}>
                  {hasAttachments && (
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <AttachmentsCard attachments={item.attachments} />
                    </Grid2>
                  )}
                  {hasInsurance && (
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <AftermarketProductsCard
                        title="Insurance"
                        icon={<Security color="primary" fontSize="small" />}
                        products={insuranceProducts}
                      />
                    </Grid2>
                  )}
                  {hasWarranty && (
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <AftermarketProductsCard
                        title="Warranty"
                        icon={<VerifiedUser color="primary" fontSize="small" />}
                        products={warrantyProducts}
                      />
                    </Grid2>
                  )}
                  {hasEquipmentFees && (
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <EquipmentChargesCard fees={fees} equipmentId={item.equipmentId} />
                    </Grid2>
                  )}
                </Grid2>
              )}

              {item.options?.length > 0 && (
                <Box>
                  <Stack direction="row" alignItems="center" gap={0.5} mb={1}>
                    <LocalOffer color="primary" fontSize="small" />
                    <Typography variant="body1" fontWeight={400}>
                      Options
                    </Typography>
                  </Stack>
                  <Stack gap={0.5}>
                    {item.options.map(option => (
                      <Stack
                        key={option.inventoryOptionId}
                        direction="row"
                        gap={2}
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Link
                          component={RouterLink}
                          to={`/inventory/options/${option.inventoryOptionId}`}
                          target="_blank"
                          rel="noopener"
                          underline="hover"
                        >
                          {option.name} ({option.code})
                        </Link>
                        <Typography variant="body2">{formatCurrency(option.cost, true)}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              )}

              {addressLine || addressLocality ? (
                <Stack gap={0.5}>
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <LocationOn color="primary" fontSize="small" />
                    <Typography variant="body1" fontWeight={400}>
                      Delivery Address
                    </Typography>
                  </Stack>
                  <Stack direction="row" gap={0.5}>
                    {addressLine && (
                      <Typography variant="body1" color="text.primary">
                        {addressLine}
                      </Typography>
                    )}
                    {addressLocality && (
                      <Typography variant="body1" color="text.secondary">
                        {addressLocality}
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              ) : null}
            </Stack>
          </AccordionDetails>
        </FlatAccordion>

        <Divider />
      </Stack>
    </Box>
  );
};
